import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaSrc, compressImage, uploadMedia, fmtTime, fmtDate } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import { AlertTriangle, MapPin, ChevronDown, Camera, CheckCircle2, User, Clock, MessageSquare, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { VoiceInputButton } from "./VoiceInputButton";

export const STATUS_LIST = ["open", "reviewing", "action_required", "resolved"];

export function StatusChip({ status, t, interactive, onSelect }) {
  const colors = {
    open: "border-[hsl(var(--issue-open))] text-[hsl(var(--issue-open))]",
    reviewing: "border-[hsl(var(--issue-reviewing))] text-[hsl(var(--issue-reviewing))]",
    action_required: "border-[hsl(var(--issue-action))] text-[hsl(var(--issue-action))]",
    resolved: "border-[hsl(var(--issue-resolved))] text-[hsl(var(--issue-resolved))]",
  };
  const label = t(`status_${status}`);
  if (!interactive) {
    return (
      <span data-testid="issue-status-chip" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${colors[status] || ""}`}>
        {label}
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="issue-status-chip-button" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${colors[status] || ""} hover:bg-muted transition-colors`}>
          {label} <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {STATUS_LIST.map((s) => (
          <DropdownMenuItem key={s} data-testid={`issue-status-option-${s}`} onClick={() => onSelect(s)} className="text-sm">
            {t(`status_${s}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PriorityBadge({ priority, t }) {
  const map = {
    high: "bg-[hsl(var(--issue-open))] text-white",
    medium: "bg-[hsl(var(--warning))] text-[hsl(var(--foreground))]",
    low: "bg-muted text-foreground",
  };
  return <Badge className={`${map[priority] || map.low} text-[10px]`}>{t(priority)}</Badge>;
}

export function IssueCard({ issue, showJob, onChanged }) {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const canManage = ["supervisor", "office", "manager", "admin"].includes(user.role);
  const hasSource = !!issue.conversation_id;

  // Viewer-language title/description (cached artifact translations)
  const trArt = issue.translations?.[lang] || {};
  const dispTitle = (trArt.title || "").trim() && trArt.title.trim() !== (issue.title || "").trim() ? trArt.title : issue.title;
  const dispDesc = (trArt.description || "").trim() && trArt.description.trim() !== (issue.description || "").trim() ? trArt.description : issue.description;

  const changeStatus = async (status) => {
    if (status === issue.status) return;
    await api.patch(`/issues/${issue.id}`, { status, user_id: user.id });
    toast.success(`${t("issue")}: ${t(`status_${status}`)}`);
    onChanged && onChanged();
  };

  // Everything is linked in Messages: clicking the issue opens the conversation where it was created
  const openInChat = () => {
    if (!hasSource) return;
    navigate(`/chat/${issue.conversation_id}${issue.message_id ? `?msg=${issue.message_id}` : ""}`);
  };

  // The person's name means there is a conversation with them — clicking opens that DM
  const openPersonDM = async (e, personId) => {
    e.stopPropagation();
    if (!personId || personId === user.id) return;
    const res = await api.post("/conversations/dm", { user_id: user.id, other_user_id: personId });
    navigate(`/chat/${res.data.id}`);
  };

  const canEdit = user.id === issue.created_by || canManage;
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const doDelete = async () => {
    try {
      await api.delete(`/issues/${issue.id}?user_id=${user.id}`);
      toast.success(t("deleted"));
      setDeleteOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <Card
      data-testid="issue-card"
      onClick={openInChat}
      className={`p-4 border-l-4 border-l-[hsl(var(--issue-open))] ${hasSource ? "cursor-pointer hover:bg-muted/40 transition-colors" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--issue-open))]" />
          <span className="font-semibold text-sm">{dispTitle}</span>
          <PriorityBadge priority={issue.priority} t={t} />
        </div>
        <span onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
          <StatusChip status={issue.status} t={t} interactive={canManage} onSelect={changeStatus} />
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="issue-menu-button" className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                  <MoreVertical className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="issue-edit-item" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4 mr-2" /> {t("edit")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="issue-delete-item" onClick={() => setDeleteOpen(true)} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" /> {t("delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </span>
      </div>
      {showJob && issue.job && (
        <div className="text-xs text-primary font-mono-plex font-semibold mt-1.5">
          #{issue.job.job_number} — {issue.job.customer_name}
        </div>
      )}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5">
        <MapPin className="h-3 w-3" /> {issue.location}
      </div>
      {dispDesc && <p className="text-sm mt-2">{dispDesc}</p>}
      {issue.media?.length > 0 && (
        <div className="flex gap-2 mt-3">
          {issue.media.map((m) => (
            <img key={m.id} src={mediaSrc(m)} alt="" className="h-20 w-20 object-cover rounded-lg border" />
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-3 pt-2 border-t">
        <button
          data-testid="issue-person-link"
          onClick={(e) => openPersonDM(e, issue.created_by)}
          className={`flex items-center gap-1 ${issue.created_by !== user.id ? "text-primary font-medium hover:underline" : ""}`}
        >
          <User className="h-3 w-3" /> {issue.created_by_name}
        </button>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(issue.created_at, lang)} {fmtTime(issue.created_at)}</span>
        {hasSource && (
          <button
            data-testid="issue-view-in-chat"
            onClick={(e) => { e.stopPropagation(); openInChat(); }}
            className="ml-auto flex items-center gap-1 text-primary font-medium hover:underline"
          >
            <MessageSquare className="h-3.5 w-3.5" /> {t("view_in_chat")}
          </button>
        )}
      </div>

      <span onClick={(e) => e.stopPropagation()}>
        <EditIssueDialog open={editOpen} onClose={() => setEditOpen(false)} issue={issue} onSaved={onChanged} />
        <ConfirmDeleteDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={doDelete} title={issue.title} t={t} testid="issue" />
      </span>
    </Card>
  );
}

function EditIssueDialog({ open, onClose, issue, onSaved }) {
  const { user, t } = useApp();
  const [title, setTitle] = useState(issue.title);
  const [location, setLocation] = useState(issue.location || "");
  const [description, setDescription] = useState(issue.description || "");
  const [priority, setPriority] = useState(issue.priority || "medium");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/issues/${issue.id}`, {
        user_id: user.id, title: title.trim(), location: location.trim(),
        description: description.trim(), priority,
      });
      toast.success(t("saved"));
      onClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-issue-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("edit")} — {t("issue")}</DialogTitle></DialogHeader>
        <Input data-testid="edit-issue-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("issue")} />
        <Input data-testid="edit-issue-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t("location")} />
        <div className="flex items-start gap-2">
          <Textarea data-testid="edit-issue-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="flex-1" />
          <VoiceInputButton onTranscript={(txt) => setDescription((prev) => (prev ? `${prev} ${txt}` : txt))} className="mt-1 shrink-0" />
        </div>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid="edit-issue-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="high">{t("priority_high")}</SelectItem>
            <SelectItem value="medium">{t("priority_medium")}</SelectItem>
            <SelectItem value="low">{t("priority_low")}</SelectItem>
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button data-testid="edit-issue-save" onClick={save} disabled={busy || !title.trim()} className="w-full">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDeleteDialog({ open, onClose, onConfirm, title, t, testid }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid={`delete-${testid}-dialog`}>
        <DialogHeader><DialogTitle className="font-display">{t("delete_confirm_q")}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{title}</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid={`delete-${testid}-cancel`} onClick={onClose}>{t("cancel")}</Button>
          <Button variant="destructive" data-testid={`delete-${testid}-confirm`} onClick={onConfirm}>
            <Trash2 className="h-4 w-4 mr-1.5" /> {t("delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TaskCard({ task, showJob, onChanged }) {
  const { user, t, lang } = useApp();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const canComplete = task.status === "open" && task.assigned_to === user.id;

  const handleCompletionPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { base64 } = await compressImage(file);
      const media = await uploadMedia({ base64, jobId: task.job_id, category: "correction", uploadedBy: user.id });
      await api.patch(`/tasks/${task.id}`, { status: "done", completion_media_id: media.id, user_id: user.id });
      toast.success(t("done"));
      onChanged && onChanged();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const thumb = task.crop_media || task.photo_media;
  const navigate = useNavigate();
  const hasSource = !!task.conversation_id;
  // Viewer-language title/description (cached artifact translations)
  const trArt = task.translations?.[lang] || {};
  const dispTitle = (trArt.title || "").trim() && trArt.title.trim() !== (task.title || "").trim() ? trArt.title : task.title;
  const dispDesc = (trArt.description || "").trim() && trArt.description.trim() !== (task.description || "").trim() ? trArt.description : task.description;
  // Everything is linked in Messages: clicking the task opens the conversation where it was assigned
  const openInChat = () => {
    if (!hasSource) return;
    navigate(`/chat/${task.conversation_id}${task.message_id ? `?msg=${task.message_id}` : ""}`);
  };
  // The person's name means there is a conversation with them — clicking opens that DM
  const openPersonDM = async (e, personId) => {
    e.stopPropagation();
    if (!personId || personId === user.id) return;
    const res = await api.post("/conversations/dm", { user_id: user.id, other_user_id: personId });
    navigate(`/chat/${res.data.id}`);
  };

  const canManage = ["supervisor", "office", "manager", "admin"].includes(user.role);
  const canEdit = user.id === task.created_by || canManage;
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const doDelete = async () => {
    try {
      await api.delete(`/tasks/${task.id}?user_id=${user.id}`);
      toast.success(t("deleted"));
      setDeleteOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <Card
      data-testid="task-card"
      onClick={openInChat}
      className={`p-4 ${task.status === "done" ? "opacity-80" : ""} ${hasSource ? "cursor-pointer hover:bg-muted/40 transition-colors" : ""}`}
    >
      <div className="flex items-start gap-3">
        {thumb && (
          <div className="relative shrink-0">
            <img src={mediaSrc(thumb)} alt="" className="h-20 w-20 object-cover rounded-lg border" />
            {task.region && task.photo_media && !task.crop_media && (
              <div
                className="absolute border-2 border-[hsl(var(--accent))] rounded-sm pointer-events-none"
                style={{
                  left: `${task.region.x * 100}%`, top: `${task.region.y * 100}%`,
                  width: `${task.region.w * 100}%`, height: `${task.region.h * 100}%`,
                }}
              />
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="font-semibold text-sm">{dispTitle}</span>
            <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Badge className={task.status === "done" ? "bg-[hsl(var(--success))] text-white text-[10px]" : "bg-accent text-accent-foreground text-[10px]"}>
                {task.status === "done" ? t("done") : t("status_open")}
              </Badge>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="task-menu-button" className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                      <MoreVertical className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem data-testid="task-edit-item" onClick={() => setEditOpen(true)}>
                      <Pencil className="h-4 w-4 mr-2" /> {t("edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="task-delete-item" onClick={() => setDeleteOpen(true)} className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" /> {t("delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </span>
          </div>
          {showJob && task.job && (
            <div className="text-xs text-primary font-mono-plex font-semibold mt-0.5">#{task.job.job_number} — {task.job.customer_name}</div>
          )}
          {dispDesc && <p className="text-xs text-muted-foreground mt-1">{dispDesc}</p>}
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-2">
            <span className="flex items-center gap-1">
              {t("assigned_to")}:{" "}
              <button
                data-testid="task-person-link"
                onClick={(e) => openPersonDM(e, task.assigned_to)}
                className={task.assigned_to !== user.id ? "font-medium text-primary hover:underline" : "font-medium text-foreground"}
              >
                {task.assigned_to_name}
              </button>
            </span>
            {task.due_label && <span>{t("due")}: {task.due_label}</span>}
            {hasSource && (
              <button
                data-testid="task-view-in-chat"
                onClick={(e) => { e.stopPropagation(); openInChat(); }}
                className="ml-auto flex items-center gap-1 text-primary font-medium hover:underline"
              >
                <MessageSquare className="h-3.5 w-3.5" /> {t("view_in_chat")}
              </button>
            )}
          </div>
        </div>
      </div>
      {task.completion_media && (
        <div className="mt-3 pt-3 border-t">
          <div className="text-[11px] font-semibold text-[hsl(var(--success))] flex items-center gap-1 mb-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t("completion_photo")}
          </div>
          <img src={mediaSrc(task.completion_media)} alt="" className="h-24 rounded-lg border object-cover" />
        </div>
      )}
      {canComplete && (
        <div className="mt-3 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCompletionPhoto} />
          <Button
            size="sm"
            data-testid="task-completion-photo-upload"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="w-full sm:w-auto"
          >
            <Camera className="h-4 w-4 mr-1.5" /> {uploading ? t("uploading") : t("complete_with_photo")}
          </Button>
        </div>
      )}

      <span onClick={(e) => e.stopPropagation()}>
        <EditTaskDialog open={editOpen} onClose={() => setEditOpen(false)} task={task} onSaved={onChanged} />
        <ConfirmDeleteDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={doDelete} title={task.title} t={t} testid="task" />
      </span>
    </Card>
  );
}

function EditTaskDialog({ open, onClose, task, onSaved }) {
  const { user, t } = useApp();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");
  const [due, setDue] = useState(task.due_label || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}`, {
        user_id: user.id, title: title.trim(), description: description.trim(), due_label: due.trim(),
      });
      toast.success(t("saved"));
      onClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-task-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("edit")} — {t("task")}</DialogTitle></DialogHeader>
        <Input data-testid="edit-task-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="flex items-start gap-2">
          <Textarea data-testid="edit-task-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="flex-1" />
          <VoiceInputButton onTranscript={(txt) => setDescription((prev) => (prev ? `${prev} ${txt}` : txt))} className="mt-1 shrink-0" />
        </div>
        <Input data-testid="edit-task-due" value={due} onChange={(e) => setDue(e.target.value)} placeholder={t("due")} />
        <DialogFooter>
          <Button data-testid="edit-task-save" onClick={save} disabled={busy || !title.trim()} className="w-full">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
